// Nominal labour times — the estimator's per-stage hours budget for a
// job, held on JobFormState.nominalStageHours and persisted as
// productionJobs.nominal_stage_hours.
//
// The form shows one field per live hours-carrying stage, except the 8
// welding bays which collapse into a single "Welding" field keyed
// WELD_NOMINAL_KEY: a spool welds in exactly one bay, so a per-bay
// budget would need the bay picked before the estimate exists. The
// server-side distributor resolves that single figure to each spool's
// own chosen bay.
import {
  WELD_NOMINAL_KEY,
  type StageDefDto,
} from "../../../../../../shared/types/production-schedule";

export interface NominalRow {
  // Map key: a live stage key, or WELD_NOMINAL_KEY.
  key: string;
  label: string;
  short: string;
  color: string;
}

export function nominalRows(catalogue: StageDefDto[]): NominalRow[] {
  const out: NominalRow[] = [];
  let weldDone = false;
  for (const def of catalogue) {
    if (def.archived) continue;
    if (!def.hasHours) continue;
    if (def.weld) {
      if (weldDone) continue;
      weldDone = true;
      out.push({
        key: WELD_NOMINAL_KEY,
        label: "Welding",
        short: "WLD",
        color: def.color,
      });
      continue;
    }
    out.push({
      key: def.stageKey,
      label: def.name,
      short: def.short,
      color: def.color,
    });
  }
  return out;
}

// Sum of the entries that will actually be distributed (> 0). Mirrors
// totalNominalHours on the server so both quote the same figure.
export function nominalTotal(map: Record<string, number>): number {
  let sum = 0;
  for (const v of Object.values(map)) {
    if (Number.isFinite(v) && v > 0) sum += v;
  }
  return Math.round(sum * 100) / 100;
}

// Apply one input edit. A blank (or invalid / negative) value deletes
// the key rather than storing 0 — "not budgeted" and "budgeted at
// zero" both mean leave the stage alone, so the map stays as small as
// the estimator's actual intent.
export function setNominalHours(
  map: Record<string, number>,
  key: string,
  raw: string,
): Record<string, number> {
  const next = { ...map };
  const trimmed = raw.trim();
  if (trimmed === "") {
    delete next[key];
    return next;
  }
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) {
    delete next[key];
    return next;
  }
  next[key] = n;
  return next;
}

// Rows carrying a budget, in form order. Used by the Spools tab
// confirm dialog to spell out the split before it writes.
export function budgetedRows(
  rows: NominalRow[],
  map: Record<string, number>,
): { row: NominalRow; hours: number }[] {
  return rows
    .map((row) => ({ row, hours: map[row.key] ?? 0 }))
    .filter((r) => r.hours > 0);
}
